package com.evoapps.evoshift.domain.usecase.owner

import com.evoapps.evoshift.domain.model.GroupSaveSummary
import com.evoapps.evoshift.domain.model.GroupSettingsResolver
import com.evoapps.evoshift.domain.model.SettingsSection
import com.evoapps.evoshift.domain.model.SettingsValues
import com.evoapps.evoshift.domain.repository.GroupSettingsRepository

/**
 * SH-269: the owner saves the group's values: only the sections that changed between [before]
 * and [after] are sent (each as a whole). Nothing changed: nothing is saved (an empty summary).
 */
class SaveGroupSettingsUseCase(private val repository: GroupSettingsRepository) {
    suspend operator fun invoke(
        groupId: String,
        before: SettingsValues,
        after: SettingsValues,
        templatesFrom: String? = null
    ): Result<GroupSaveSummary> {
        val changed = GroupSettingsResolver.changed(before, after)
            // The finances' sections only when they could be read (null financials: not shown).
            .filterTo(mutableSetOf()) { it.storage == SettingsSection.Storage.SETTINGS || after.financials != null }
        if (changed.isEmpty() && templatesFrom == null) return Result.success(GroupSaveSummary(emptyMap(), emptyMap()))
        return repository.saveGroupSettings(groupId, changed, after, templatesFrom)
    }
}

/**
 * SH-269: "Reset to group" (true: the group's values replace the workplace's, the section follows
 * the group) or "Own settings" (false: it keeps the values it has, a copy to change) per section.
 */
class SetSectionInheritanceUseCase(private val repository: GroupSettingsRepository) {
    suspend operator fun invoke(workplaceId: String, sections: Map<SettingsSection, Boolean>): Result<Set<SettingsSection>> =
        if (sections.isEmpty()) Result.success(emptySet()) else repository.setInheritance(workplaceId, sections)
}
